import React, { useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { ApiError, updateCustomerProfile } from '../../api';
import PhoneField from '../../components/PhoneField';
import EmailManager from '../../components/account/EmailManager';
import ChangePasswordCard from '../../components/account/ChangePasswordCard';

const inputCls =
  'bg-field text-fg placeholder:text-fg-subtle w-full rounded-lg border border-default px-3 py-2 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-600';
const labelCls = 'mb-1 block text-xs font-semibold tracking-wide text-fg-secondary uppercase';

export default function Account() {
  const { session, refresh } = useAuth();
  const customer = session?.customer;

  const [name, setName] = useState(customer?.name ?? '');
  const [phone, setPhone] = useState<string | undefined>(customer?.phone || undefined);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const saveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await updateCustomerProfile({ name: name.trim(), phone: phone || '' });
      await refresh();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save your profile.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6 sm:py-10">
      <h1 className="font-display text-fg mb-6 text-2xl font-semibold">Account</h1>
      <div className="space-y-6">
        {/* Profile */}
        <div className="border-default bg-surface overflow-hidden rounded-xl border shadow-sm">
          <div className="border-subtle flex items-center justify-between gap-3 border-b px-4 py-4 sm:px-6 sm:py-5">
            <div>
              <h2 className="text-fg text-base font-bold sm:text-lg">Profile</h2>
              <p className="text-fg-muted mt-0.5 hidden text-xs sm:block sm:text-sm">
                Your name and contact number
              </p>
            </div>
            {saved && (
              <span className="text-success flex shrink-0 items-center gap-1 text-xs font-medium sm:text-sm">
                <Check className="h-3.5 w-3.5" />
                Saved
              </span>
            )}
          </div>
          <form onSubmit={saveProfile} className="space-y-3 px-4 py-4 sm:px-6">
            {error && <p className="text-danger text-sm font-medium">{error}</p>}
            <div>
              <label htmlFor="acc-name" className={labelCls}>
                Full name
              </label>
              <input
                id="acc-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Phone number</label>
              <PhoneField
                value={phone}
                onChange={setPhone}
                selectClassName="rounded-lg border border-default px-3 py-2 text-sm"
                inputClassName="flex-1 rounded-lg border border-default px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand-600"
              />
            </div>
            <button
              type="submit"
              disabled={saving}
              className="bg-brand-600 hover:bg-brand-700 flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors disabled:opacity-50"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Save profile
            </button>
          </form>
        </div>

        <EmailManager
          email={customer?.email ?? ''}
          emailVerified={Boolean(session?.emailVerified)}
        />
        {session?.hasPassword && <ChangePasswordCard />}
      </div>
    </div>
  );
}
